import {useState} from "react";
import {FieldValues} from "react-hook-form";
import {filterWZs, searchWZs} from "./api";
import {downloadWZFile} from "../api";
import {saveWZFile} from "../download";
import {WZDownloadFormat} from "../types";
import {WZApiError} from "../api";
import {WZFormSubmit, WZListItem} from "./types";
import {normalizeWZResponse} from "./helpers";

type UseWZListResult = {
    wzs: WZListItem[];
    loading: boolean;
    requestError: boolean;
    requestErrorStatus: number | null;
    downloadError: boolean;
    downloading: string | null;
    filterRequest: WZFormSubmit;
    searchRequest: WZFormSubmit;
    downloadRequest: (id: number, format: WZDownloadFormat) => Promise<void>;
};

export function useWZList(initialWzs: WZListItem[] | null): UseWZListResult {
    const [wzs, setWzs] = useState<WZListItem[]>(normalizeWZResponse(initialWzs));
    const [loading, setLoading] = useState(false);
    const [requestError, setRequestError] = useState(false);
    const [requestErrorStatus, setRequestErrorStatus] = useState<number | null>(null);
    const [downloadError, setDownloadError] = useState(false);
    const [downloading, setDownloading] = useState<string | null>(null);

    async function requestList(request: (values: FieldValues) => Promise<WZListItem[]>, values: FieldValues): Promise<void> {
        setLoading(true);
        setRequestError(false);
        setRequestErrorStatus(null);

        try {
            setWzs(await request(values));
        } catch (error) {
            setRequestError(true);
            setRequestErrorStatus(error instanceof WZApiError ? error.status : null);
        } finally {
            setLoading(false);
        }
    }

    async function downloadRequest(id: number, format: WZDownloadFormat): Promise<void> {
        setDownloading(id + '-' + format);
        setDownloadError(false);

        try {
            saveWZFile(await downloadWZFile(id, format));
        } catch {
            setDownloadError(true);
        } finally {
            setDownloading(null);
        }
    }

    return {
        wzs,
        loading,
        requestError,
        requestErrorStatus,
        downloadError,
        downloading,
        filterRequest: (values: FieldValues) => requestList(filterWZs, values),
        searchRequest: (values: FieldValues) => requestList(searchWZs, values),
        downloadRequest,
    };
}
